/**
 * Customer Portal Store Configuration
 *
 * Combines all domain slices and sagas into a single store.
 * Exports store, types, and hooks for use throughout the app.
 */

import { configureStore, combineReducers, createSagaMiddleware, all, fork, configureTenantIdProvider, configureFactoryIdProvider, configureLocationIdProvider, put, takeLatest } from '@ufc/core'

// Domain imports - slices and sagas (MVP: removed projects and users)
import { ordersReducer, ordersSaga, ordersActions, DEFAULT_ORDERS_FILTER } from '@ufc/domains/orders'
import { salesOrdersReducer, salesOrdersSaga } from '@ufc/domains/sales-orders'
import { assetsReducer, assetsSaga } from '@ufc/domains/assets'
import { shipmentsReducer, shipmentsSaga } from '@ufc/domains/shipments'
import { addressbookReducer, addressbookSaga, addressbookActions } from '@ufc/domains/addressbook'
import { financeReducer, financeSaga } from '@ufc/domains/finance'
import { authReducer, authSaga, authActions } from '@ufc/domains/auth'
import { pricingReducer, pricingSaga } from '@ufc/domains/pricing'
import { factoriesReducer, factoriesSaga, factoriesActions } from '@ufc/domains/factories'
import { locationsReducer, locationsSaga, locationsActions, selectLocationContext, selectLocationDerivedFactoryContext } from '@ufc/domains/locations'
import { adminContextReducer, adminContextActions, adminContextSaga, selectTenantContext } from '@ufc/domains/admin-context'
import { tenantsReducer, tenantsSaga } from '@ufc/domains/tenants'
import { usersReducer, usersSaga } from '@ufc/domains/users'
import { customerAccountsReducer, customerAccountsSaga } from '@ufc/domains/customer-accounts'
import { initReducer, initSaga } from '@ufc/domains/portal-init'
import { regionsReducer, regionsSaga } from '@ufc/domains/regions'
import { citiesReducer, citiesSaga } from '@ufc/domains/cities'
import { availabilityReducer, availabilitySaga } from '@ufc/domains/availability'
import { productsReducer, productsSaga } from '@ufc/domains/products'
import { customerPortalDashboardReducer, customerPortalDashboardSaga } from '@ufc/domains/dashboards/customer-portal'
import { globalSearchReducer, globalSearchSaga } from '@ufc/domains/global-search'
import { supportReducer, supportSaga } from '@ufc/domains/support'
import { tripPlanningLiveReducer, tripPlanningLiveSaga } from '@ufc/domains/trip-planning'
import { PRIVACY_POLICY_VERSION } from '@/lib/policy'

// Analytics domain
import {
  analyticsReducer,
  consentReducer,
  consentActions,
  analyticsSaga,
  createAnalyticsMiddleware,
} from '@ufc/domains/analytics'

// Create analytics middleware
const analyticsMiddleware = createAnalyticsMiddleware({
  excludeActions: ['sidebar/toggle', 'theme/setTheme', 'drawer/'],
  debug: import.meta.env.DEV,
})

function cloneDefaultOrdersFilter() {
  return {
    ...DEFAULT_ORDERS_FILTER,
    status: [...(DEFAULT_ORDERS_FILTER.status ?? [])],
    products: [...(DEFAULT_ORDERS_FILTER.products ?? [])],
    deliveryAddresses: [...(DEFAULT_ORDERS_FILTER.deliveryAddresses ?? [])],
  }
}

// Root reducer combining all domain slices (MVP)
const appReducer = combineReducers({
  // Auth (first for quick access)
  auth: authReducer,
  // Portal initialization (profile, account, settings, permissions)
  portalInit: initReducer,
  // Admin context (for super admin tenant switching)
  adminContext: adminContextReducer,
  // Business domains
  orders: ordersReducer,
  salesOrders: salesOrdersReducer,
  assets: assetsReducer,
  shipments: shipmentsReducer,
  addressbook: addressbookReducer,
  finance: financeReducer,
  pricing: pricingReducer,
  factories: factoriesReducer,
  locations: locationsReducer,
  // Tenants (for super admin)
  tenants: tenantsReducer,
  // Users (for admin user management)
  users: usersReducer,
  // Customer Accounts (for admin financial management)
  customerAccounts: customerAccountsReducer,
  // Regions (for admin configuration)
  regions: regionsReducer,
  // Cities (for admin configuration)
  cities: citiesReducer,
  // Availability (for admin factory-variant configuration)
  availability: availabilityReducer,
  // Products (for admin SKU/Variant management)
  products: productsReducer,
  // Analytics & Consent
  analytics: analyticsReducer,
  consent: consentReducer,
  // Customer Portal Dashboard
  customerPortalDashboard: customerPortalDashboardReducer,
  // Global Search
  globalSearch: globalSearchReducer,
  // Support
  support: supportReducer,
  tripPlanningLive: tripPlanningLiveReducer,
})

type AppState = ReturnType<typeof appReducer>
const reduceAppState = appReducer as (
  state: AppState | undefined,
  action: { type: string; payload?: unknown }
) => AppState

function hasFactoryContextChanged(
  state: AppState | undefined,
  action: { type: string; payload?: unknown }
): boolean {
  if (!state) return false

  if (action.type === locationsActions.selectLocation.type) {
    const payload = action.payload as { documentId?: string; factoryDocumentId?: string } | null | undefined
    const nextLocationId = payload?.documentId ?? null
    const nextFactoryId = payload?.factoryDocumentId ?? null

    return (
      state.locations.currentLocationId !== nextLocationId ||
      state.locations.derivedFactoryId !== nextFactoryId
    )
  }

  if (action.type === locationsActions.clearLocationSelection.type) {
    return state.locations.currentLocationId !== null || state.locations.derivedFactoryId !== null
  }

  if (action.type === factoriesActions.selectFactory.type) {
    const payload = action.payload as { documentId?: string } | null | undefined
    const nextFactoryId = payload?.documentId ?? null
    return state.factories.currentFactoryId !== nextFactoryId
  }

  if (action.type === factoriesActions.clearFactorySelection.type) {
    return state.factories.currentFactoryId !== null
  }

  return false
}

function resetFactoryScopedState(state: AppState): AppState {
  return {
    ...state,
    orders: undefined,
    salesOrders: undefined,
    assets: undefined,
    shipments: undefined,
    addressbook: undefined,
    finance: undefined,
    pricing: undefined,
    tenants: undefined,
    users: undefined,
    customerAccounts: undefined,
    regions: undefined,
    cities: undefined,
    availability: undefined,
    products: undefined,
    customerPortalDashboard: undefined,
    globalSearch: undefined,
  } as unknown as AppState
}

function rootReducer(
  state: AppState | undefined,
  action: { type: string; payload?: unknown }
): AppState {
  const nextState = state && hasFactoryContextChanged(state, action)
    ? resetFactoryScopedState(state)
    : state

  return reduceAppState(nextState, action)
}

function* cleanupOnLogoutSaga() {
  // Clear user-scoped state that must not leak across sessions.
  yield put(addressbookActions.clearAddresses())
  yield put(ordersActions.setFilter(cloneDefaultOrdersFilter()))
  yield put(ordersActions.clearOrders())
  yield put(ordersActions.clearSelection())
}

function* watchCleanupOnLogoutSaga() {
  yield takeLatest(authActions.logout.type, cleanupOnLogoutSaga)
}

// Root saga combining all domain sagas (MVP)
function* rootSaga() {
  yield all([
    // Auth saga (first to initialize quickly)
    fork(authSaga),
    // Portal init saga (runs after auth to fetch init data)
    fork(initSaga),
    // Admin context saga (fetches tenant list for admin users)
    fork(adminContextSaga),
    // Business domain sagas
    fork(ordersSaga),
    fork(salesOrdersSaga),
    fork(assetsSaga),
    fork(shipmentsSaga),
    fork(addressbookSaga),
    fork(financeSaga),
    fork(pricingSaga),
    fork(factoriesSaga),
    fork(locationsSaga),
    // Tenants saga (for super admin)
    fork(tenantsSaga),
    // Users saga (for admin user management)
    fork(usersSaga),
    // Customer Accounts saga (for admin financial management)
    fork(customerAccountsSaga),
    // Regions saga (for admin configuration)
    fork(regionsSaga),
    // Cities saga (for admin configuration)
    fork(citiesSaga),
    // Availability saga (for admin factory-variant configuration)
    fork(availabilitySaga),
    // Products saga (for admin SKU/Variant management)
    fork(productsSaga),
    // Analytics saga
    fork(analyticsSaga),
    // Customer Portal Dashboard saga
    fork(customerPortalDashboardSaga),
    // Global Search saga
    fork(globalSearchSaga),
    // Support saga
    fork(supportSaga),
    // Customer delivery tracking
    fork(tripPlanningLiveSaga),
    // App-level cleanup
    fork(watchCleanupOnLogoutSaga),
  ])
}

// Create saga middleware
const sagaMiddleware = createSagaMiddleware()

// Create store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: false, // We use sagas instead
      serializableCheck: {
        // Ignore these action types for serializable check
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    })
      .concat(sagaMiddleware)
      .concat(analyticsMiddleware),
  devTools: import.meta.env.DEV,
})

// Run the root saga
sagaMiddleware.run(rootSaga)

// Load cookie/consent preferences from localStorage on startup
store.dispatch(consentActions.loadStoredConsent({ policyVersion: PRIVACY_POLICY_VERSION }))

// Restore admin context tenant selection from localStorage
// Note: isAdmin state will be set after auth initialization based on user role
store.dispatch(adminContextActions.restoreFromStorage())

// Configure HTTP client to include X-Tenant-Id header when admin has selected a tenant
// This ensures all API calls are scoped to the selected tenant
configureTenantIdProvider({
  getTenantId: () => selectTenantContext(store.getState()),
})

// Restore factory selection from localStorage
store.dispatch(factoriesActions.restoreFactoryFromStorage())

// Fetch factories list for header dropdown
store.dispatch(factoriesActions.fetchFactoriesList())

// Restore location selection and fetch locations list
store.dispatch(locationsActions.restoreLocationFromStorage())
store.dispatch(locationsActions.fetchLocationsList())

// Configure HTTP client headers
configureFactoryIdProvider({
  getFactoryId: () => selectLocationDerivedFactoryContext(store.getState()),
})

configureLocationIdProvider({
  getLocationId: () => selectLocationContext(store.getState()),
})

// Export types
export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch

// Re-export domain selectors for convenience
export * from '@ufc/domains/orders'
